/**
 * Notification Service
 * 
 * API service for HMS Notification Center
 * Handles all notification-related API calls
 * 
 * Endpoints:
 * - GET    /api/notifications/          - List notifications
 * - GET    /api/notifications/{id}/     - Get specific notification
 * - PATCH  /api/notifications/{id}/     - Update notification
 * - DELETE /api/notifications/{id}/     - Delete notification
 * - POST   /api/notifications/{id}/mark_read/ - Mark as read
 * - POST   /api/notifications/mark_all_read/ - Mark all as read
 * - GET    /api/notifications/unread_count/ - Get unread count
 * - GET    /api/notifications/stats/ - Get statistics
 */

import axiosInstance from '../utils/axiosConfig';

const notificationService = {
  /**
   * Get list of notifications with optional filters
   * 
   * @param {Object} filters - Filter parameters
   * @param {string} filters.status - Filter by status (unread, read, archived)
   * @param {string} filters.type - Filter by notification type
   * @param {string} filters.priority - Filter by priority (low, normal, urgent)
   * @param {boolean} filters.action_required - Filter by action required
   * @returns {Promise<Array>} List of notifications
   */
  async getNotifications(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.type && filters.type !== 'all') {
        params.append('type', filters.type);
      }
      if (filters.priority && filters.priority !== 'all') {
        params.append('priority', filters.priority);
      }
      if (filters.action_required !== undefined) {
        params.append('action_required', filters.action_required);
      }

      const queryString = params.toString();
      const url = queryString ? `/api/notifications/?${queryString}` : '/api/notifications/';
      
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  /**
   * Get a specific notification by ID
   * 
   * @param {number} id - Notification ID
   * @returns {Promise<Object>} Notification object
   */
  async getNotificationById(id) {
    try {
      const response = await axiosInstance.get(`/api/notifications/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching notification ${id}:`, error);
      throw error;
    }
  },

  /**
   * Mark a specific notification as read
   * 
   * @param {number} id - Notification ID
   * @returns {Promise<Object>} Updated notification
   */
  async markAsRead(id) {
    try {
      const response = await axiosInstance.post(`/api/notifications/${id}/mark_read/`);
      return response.data;
    } catch (error) {
      console.error(`Error marking notification ${id} as read:`, error);
      throw error;
    }
  },

  /**
   * Mark all unread notifications as read
   * 
   * @returns {Promise<Object>} Response with count of updated notifications
   */
  async markAllAsRead() {
    try {
      const response = await axiosInstance.post('/api/notifications/mark_all_read/');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  /**
   * Archive a specific notification
   * 
   * @param {number} id - Notification ID
   * @returns {Promise<Object>} Updated notification
   */
  async markAsArchived(id) {
    try {
      const response = await axiosInstance.post(`/api/notifications/${id}/mark_archived/`);
      return response.data;
    } catch (error) {
      console.error(`Error archiving notification ${id}:`, error);
      throw error;
    }
  },

  /**
   * Mark a specific notification as unread
   * 
   * @param {number} id - Notification ID
   * @returns {Promise<Object>} Updated notification
   */
  async markAsUnread(id) {
    try {
      const response = await axiosInstance.post(`/api/notifications/${id}/mark_unread/`);
      return response.data;
    } catch (error) {
      console.error(`Error marking notification ${id} as unread:`, error);
      throw error;
    }
  },

  /**
   * Delete a notification
   * 
   * @param {number} id - Notification ID
   * @returns {Promise<void>}
   */
  async deleteNotification(id) {
    try {
      await axiosInstance.delete(`/api/notifications/${id}/`);
    } catch (error) {
      console.error(`Error deleting notification ${id}:`, error);
      throw error;
    }
  },

  /**
   * Update a notification
   * 
   * @param {number} id - Notification ID
   * @param {Object} data - Update data
   * @returns {Promise<Object>} Updated notification
   */
  async updateNotification(id, data) {
    try {
      const response = await axiosInstance.patch(`/api/notifications/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating notification ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get count of unread notifications
   * 
   * @returns {Promise<number>} Unread count
   */
  async getUnreadCount() {
    try {
      const response = await axiosInstance.get('/api/notifications/unread_count/');
      return response.data.unread_count;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  },

  /**
   * Get notification statistics
   * 
   * @returns {Promise<Object>} Statistics object
   */
  async getStats() {
    try {
      const response = await axiosInstance.get('/api/notifications/stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  }
};

export default notificationService;
